/**
 * Admin Payments Page
 * View all payments and subscriptions
 */
import { useLanguage } from "@/context/AppContext";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  ArrowLeft, CreditCard, TrendingUp, Users, Clock, CheckCircle, XCircle, Loader2, RefreshCw
} from "lucide-react";

import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AdminPayments = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [payments, setPayments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [subStats, setSubStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completingPayment, setCompletingPayment] = useState(null);
  const [activeTab, setActiveTab] = useState("payments");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [paymentsRes, subsRes] = await Promise.all([
        axios.get(`${API}/admin/payments`, { withCredentials: true }),
        axios.get(`${API}/admin/subscriptions`, { withCredentials: true })
      ]);
      setPayments(paymentsRes.data.payments);
      setPaymentStats(paymentsRes.data.stats);
      setSubscriptions(subsRes.data.subscriptions);
      setSubStats(subsRes.data.stats);
    } catch (error) {
      console.error("Failed to load payment data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatAmount = (cents) => {
    return `CHF ${(cents / 100).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "captured":
      case "completed":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />{t('adminStatusCompleted')}</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />{t('adminStatusPending')}</Badge>;
      case "failed":
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />{t('adminStatusFailed')}</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">{status}</Badge>;
    }
  };

  const handleCompletePayment = async (paymentId) => {
    if (!window.confirm('Zahlung als abgeschlossen markieren und Abonnement aktivieren?')) return;
    
    setCompletingPayment(paymentId);
    try {
      const res = await axios.post(`${API}/admin/complete-payment/${paymentId}`, {}, { withCredentials: true });
      if (res.data.success) {
        toast.success(res.data.message);
        loadData(); // Reload data
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Abschliessen');
    } finally {
      setCompletingPayment(null);
    }
  };

  const handleCheckAllPending = async () => {
    if (!window.confirm('Alle ausstehenden Zahlungen bei Saferpay prüfen und automatisch abschliessen?')) return;
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/check-pending-payments`, {}, { withCredentials: true });
      toast.success(`Geprüft: ${res.data.checked}, Abgeschlossen: ${res.data.completed}`);
      loadData(); // Reload data
    } catch (error) {
      toast.error('Fehler beim Prüfen');
    } finally {
      setLoading(false);
    }
  };

  const getPlanBadge = (plan) => {
    if (plan === "monthly") {
      return <Badge className="bg-blue-100 text-blue-700">{t('adminPlanMonthly')}</Badge>;
    }
    return <Badge className="bg-purple-100 text-purple-700">{t('adminPlanYearly')}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen infirmly-dark infirmly-dark flex items-center justify-center" style={{ background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)" }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen infirmly-dark"
      style={{ background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)" }}
    >
      {/* Header */}
      <header className="border-b border-white/10 bg-white/[0.04] border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('adminBack')}
            </Button>
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
                {t('adminPaymentsTitle')}
              </h1>
              <p className="text-sm text-gray-500">{t('adminPaymentsSubtitle')}</p>
            </div>
          </div>
          <Button onClick={loadData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('adminRefresh')}
          </Button>
          <Button onClick={handleCheckAllPending} variant="default" size="sm" className="bg-green-600 hover:bg-green-700">
            <CheckCircle className="w-4 h-4 mr-2" />
            {t('adminCheckAll')}
          </Button>
        </div>
      </header>

      <main className="p-6 max-w-7xl mx-auto">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('adminKpiRevenue')}</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
                    CHF {paymentStats?.total_revenue_chf?.toFixed(2) || "0.00"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('adminKpiActiveSubs')}</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
                    {subStats?.total_subscribers || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-full">
                  <CreditCard className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">MRR</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
                    CHF {subStats?.mrr_chf?.toFixed(2) || "0.00"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-yellow-100 rounded-full">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('adminKpiPending')}</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
                    {paymentStats?.pending || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="payments">
              {t('adminTabPayments')} ({paymentStats?.total || 0})
            </TabsTrigger>
            <TabsTrigger value="subscriptions">
              {t('adminTabSubscriptions')} ({subStats?.total_subscribers || 0})
            </TabsTrigger>
          </TabsList>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>{t('adminTabPayments')}</CardTitle>
              </CardHeader>
              <CardContent>
                {payments.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>Noch keine Zahlungen vorhanden</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('adminColDate')}</TableHead>
                        <TableHead>{t('adminColUser')}</TableHead>
                        <TableHead>{t('adminColType')}</TableHead>
                        <TableHead>{t('adminColAmount')}</TableHead>
                        <TableHead>{t('adminColStatus')}</TableHead>
                        <TableHead>{t('adminColPaymentId')}</TableHead>
                        <TableHead>{t('adminColAction')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.payment_id}>
                          <TableCell className="text-sm">
                            {formatDate(payment.created_at)}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{payment.user_name || "Unbekannt"}</p>
                              <p className="text-xs text-gray-500">{payment.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {payment.type?.includes("monthly") ? (
                              <Badge className="bg-blue-100 text-blue-700">{t('adminPlanMonthly')}</Badge>
                            ) : payment.type?.includes("annual") ? (
                              <Badge className="bg-purple-100 text-purple-700">{t('adminPlanYearly')}</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-700">{payment.type}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {formatAmount(payment.amount)}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(payment.status)}
                          </TableCell>
                          <TableCell className="text-xs text-gray-500 font-mono">
                            {payment.payment_id}
                          </TableCell>
                          <TableCell>
                            {payment.status === "pending" && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-green-600 border-green-300 hover:bg-green-50"
                                onClick={() => handleCompletePayment(payment.payment_id)}
                                disabled={completingPayment === payment.payment_id}
                              >
                                {completingPayment === payment.payment_id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <><CheckCircle className="w-3 h-3 mr-1" /> Abschliessen</>
                                )}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Aktive Abonnements</CardTitle>
                  <div className="flex gap-4 text-sm">
                    <span className="text-blue-600">
                      Monatlich: {subStats?.monthly_subscribers || 0}
                    </span>
                    <span className="text-purple-600">
                      Jährlich: {subStats?.annual_subscribers || 0}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {subscriptions.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>Noch keine aktiven Abonnements</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Benutzer</TableHead>
                        <TableHead>E-Mail</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Gültig bis</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscriptions.map((sub) => (
                        <TableRow key={sub.user_id}>
                          <TableCell className="font-medium">{sub.name}</TableCell>
                          <TableCell className="text-gray-500">{sub.email}</TableCell>
                          <TableCell>{getPlanBadge(sub.subscription)}</TableCell>
                          <TableCell>
                            {sub.subscription_expires ? (
                              formatDate(sub.subscription_expires)
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {sub.subscription_expires && new Date(sub.subscription_expires) > new Date() ? (
                              <Badge className="bg-green-100 text-green-700">Aktiv</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700">Abgelaufen</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPayments;
